import React from 'react';
import styled from 'styled-components';
import { TFunction, useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import { ContentLoader, DividedBox, Panel, Typography } from '@dev/ee-components';
import { isEmpty } from 'lodash-es';
import { useSelector } from 'react-redux';
import { JourneySummary } from '@dev/ee-api/ee-api-models';

import useMobile from '@/services/hooks/useMobile';
import { formatNumber } from '@/services/tools/formatNumber';
import { getAutomationStore } from '@/store/automations/actions';

import { StyledProps } from '../../AutomationReportInterface';

const PanelsWrapper = styled.div<StyledProps>`
    display: ${({ isMobile }) => (isMobile ? 'flex' : 'grid')};
    flex-direction: ${({ isMobile }) => (isMobile ? 'column' : '')};
    column-gap: 30px;
    grid-template-columns: ${({ isMobile }) => (isMobile ? '' : '1fr 1fr 1fr 1fr')};
    padding-top: 32px;

    .GridCart:first-child {
        margin-top: 0px;
    }
    .GridCart {
        margin-top: ${({ isMobile }) => (isMobile ? 24 : '')}px;
    }
`;

const ContentLoaderWrapper = styled.div`
    margin-right: 16px;
    min-width: 45px;
    flex: 1;
`;

const PerformanceBasicInfoWrapper = styled.div`
    display: flex;
    width: 100%;
    align-items: baseline;
    .performance-label {
        margin-left: 12px;
    }
`;

const PerformanceDividedWrapper = styled.div<StyledProps>`
    padding-top: 32px;
    padding-bottom: 32;
    .row {
        margin: 0;
    }
    .dividedstats {
        .MuiTypography-root {
            div {
                display: inline;
            }
        }
    }
    .dividedMargin {
        .MuiTypography-root {
            &:first-child {
                margin-right: 6px;
            }
        }
    }

    .gridCard {
        margin-bottom: ${({ isMobile }) => (isMobile ? 24 : '')}px;
    }
`;

const PerformanceDividedBoxHeader = styled.div`
    margin-right: '32px';
    width: '85px';
`;

const PanelContentLoader = (): JSX.Element => (
    <ContentLoaderWrapper>
        <ContentLoader mode="row" height={16} />
        <ContentLoader mode="row" height={12} />
    </ContentLoaderWrapper>
);

const getPerformanceDividedBoxes = (
    singleAutomation: JourneySummary,
    loadingSingleAutomation: boolean,
    t: TFunction<'translation'>
) => [
    [
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('delivered')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats dividedMargin">
                        <Typography variant="body1" bodyVariant="help" display="inline">
                            {singleAutomation?.AverageDeliverRate}%{' '}
                        </Typography>
                        <Typography display="inline" variant="h4">
                            {formatNumber(singleAutomation?.TotalDelivered)}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('openrate')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats">
                        <Typography display="inline" variant="h4">
                            {`${singleAutomation?.AverageOpenRate}%`}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
    ],
    [
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('complained')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats dividedMargin">
                        <Typography variant="body1" bodyVariant="help" display="inline">
                            {singleAutomation?.AverageComplaintRate}%{' '}
                        </Typography>
                        <Typography display="inline" variant="h4">
                            {formatNumber(singleAutomation?.TotalComplained)}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('clickrate')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats">
                        <Typography display="inline" variant="h4">
                            {`${singleAutomation?.AverageClickRate}%`}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
    ],
];

const getMobilePerformanceDividedBoxesPartI = (
    singleAutomation: JourneySummary,
    loadingSingleAutomation: boolean,
    t: TFunction<'translation'>
) => [
    [
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('delivered')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats dividedMargin">
                        <Typography variant="body1" bodyVariant="help" display="inline">
                            {singleAutomation?.AverageDeliverRate}%{' '}
                        </Typography>
                        <Typography display="inline" variant="h4">
                            {formatNumber(singleAutomation?.TotalDelivered)}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('complained')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats dividedMargin">
                        <Typography variant="body1" bodyVariant="help" display="inline">
                            {singleAutomation?.AverageComplaintRate}%{' '}
                        </Typography>
                        <Typography display="inline" variant="h4">
                            {formatNumber(singleAutomation?.TotalComplained)}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
    ],
];

const getMobilePerformanceDividedBoxesPartII = (
    singleAutomation: JourneySummary,
    loadingSingleAutomation: boolean,
    t: TFunction<'translation'>
) => [
    [
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('openrate')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats">
                        <Typography display="inline" variant="h4">
                            {`${singleAutomation?.AverageOpenRate}%`}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
        {
            label: (
                <PerformanceDividedBoxHeader>
                    <Typography variant="body2">{t('clickrate')}</Typography>
                </PerformanceDividedBoxHeader>
            ),
            value:
                !loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                    <div className="dividedstats">
                        <Typography display="inline" variant="h4">
                            {`${singleAutomation?.AverageClickRate}%`}
                        </Typography>
                    </div>
                ) : (
                    <ContentLoaderWrapper>
                        <ContentLoader mode="row" height={16} />
                    </ContentLoaderWrapper>
                ),
        },
    ],
];

export const PerformanceBasicInfo = (): JSX.Element => {
    const { t } = useTranslation();
    const { singleAutomation, loadingSingleAutomation } = useSelector(getAutomationStore);
    const isMobile = useMobile();

    return (
        <Box paddingTop="16px" paddingBottom="16px">
            <Typography variant="h1">{t('performance')}</Typography>

            <PanelsWrapper isMobile={isMobile}>
                <Panel
                    description={
                        <div>
                            {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                <>
                                    <PerformanceBasicInfoWrapper>
                                        <Typography variant="h1">
                                            {formatNumber(singleAutomation?.TotalOpened)}
                                        </Typography>
                                        <Typography className="performance-label" variant="h3">
                                            {t('opened')}
                                        </Typography>
                                    </PerformanceBasicInfoWrapper>
                                    <div>
                                        <Typography variant="body1" bodyVariant="help">
                                            {`${singleAutomation?.AverageOpenRate}%`}
                                        </Typography>
                                    </div>
                                </>
                            ) : (
                                <PanelContentLoader />
                            )}
                        </div>
                    }
                    headerPadding="24px 32px"
                />
                <Panel
                    description={
                        <div>
                            {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                <>
                                    <PerformanceBasicInfoWrapper>
                                        <Typography variant="h1">
                                            {formatNumber(singleAutomation?.TotalClicked)}
                                        </Typography>
                                        <Typography className="performance-label" variant="h3">
                                            {t('clicked')}
                                        </Typography>
                                    </PerformanceBasicInfoWrapper>
                                    <div>
                                        <Typography variant="body1" bodyVariant="help">
                                            {`${singleAutomation?.AverageClickRate}%`}
                                        </Typography>
                                    </div>
                                </>
                            ) : (
                                <PanelContentLoader />
                            )}
                        </div>
                    }
                    headerPadding="24px 32px"
                />
                <Panel
                    description={
                        <div>
                            {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                <>
                                    <PerformanceBasicInfoWrapper>
                                        <Typography variant="h1">
                                            {formatNumber(singleAutomation?.TotalBounced)}
                                        </Typography>
                                        <Typography className="performance-label" variant="h3">
                                            {t('bounced')}
                                        </Typography>
                                    </PerformanceBasicInfoWrapper>
                                    <div>
                                        <Typography variant="body1" bodyVariant="help">
                                            {`${singleAutomation?.AverageBounceRate}%`}
                                        </Typography>
                                    </div>
                                </>
                            ) : (
                                <PanelContentLoader />
                            )}
                        </div>
                    }
                    headerPadding="24px 32px"
                />
                <Panel
                    description={
                        <div>
                            {!loadingSingleAutomation && !isEmpty(singleAutomation) ? (
                                <>
                                    <PerformanceBasicInfoWrapper>
                                        <Typography variant="h1">
                                            {formatNumber(singleAutomation?.TotalUnsubscribed)}
                                        </Typography>
                                        <Typography className="performance-label" variant="h3">
                                            {t('unsubscribed')}
                                        </Typography>
                                    </PerformanceBasicInfoWrapper>
                                    <div>
                                        <Typography variant="body1" bodyVariant="help">
                                            {`${singleAutomation?.AverageUnsubscribeRate}%`}
                                        </Typography>
                                    </div>
                                </>
                            ) : (
                                <PanelContentLoader />
                            )}
                        </div>
                    }
                    headerPadding="24px 32px"
                />
            </PanelsWrapper>

            <PerformanceDividedWrapper isMobile={isMobile}>
                {!isMobile ? (
                    <DividedBox rows={getPerformanceDividedBoxes(singleAutomation, loadingSingleAutomation, t)} />
                ) : (
                    <>
                        <DividedBox
                            rows={getMobilePerformanceDividedBoxesPartI(singleAutomation, loadingSingleAutomation, t)}
                        />
                        <DividedBox
                            rows={getMobilePerformanceDividedBoxesPartII(singleAutomation, loadingSingleAutomation, t)}
                        />
                    </>
                )}
            </PerformanceDividedWrapper>
        </Box>
    );
};
